/**
 * Closing sidebar type scale — six sizes, three weights, two families
 * (CLOSING_SIDEBAR_DESIGN.md, "Typography Guide"). Every text in the drawer and
 * the Readiness tab is one of these tokens; hierarchy beyond them comes from
 * colour and position, not new font styles.
 *
 * Inter is loaded as a variable font (public/index.html: wght 100..900), so the
 * in-between weights (450 / 550 / 620 / 650) render as specified rather than
 * snapping to the nearest static face.
 *
 * Use as `sx={TYPE.row}` / `sx={{ ...TYPE.meta, color: … }}`.
 */
import { slate, status, teal } from "../../theme/colors";

export const FONT_UI = 'Inter, -apple-system, "Segoe UI", Roboto, sans-serif';
export const FONT_MONO = '"SF Mono", ui-monospace, Menlo, Consolas, monospace';

// Ink roles for this surface (the guide's --ink / --slate / --muted).
export const INK = slate[900];
export const SLATE = slate[600];
export const MUTED = slate[400];

const ui = { fontFamily: FONT_UI };
const mono = { fontFamily: FONT_MONO, fontVariantNumeric: "tabular-nums" };

export const TYPE = {
  // Borrower name — the panel's single largest text.
  title: { ...ui, fontSize: 20, fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.01em", color: INK },
  // Readiness status heading inside the body ("Clear To Close").
  status: { ...ui, fontSize: 17, fontWeight: 700, lineHeight: 1.3, letterSpacing: "-0.005em", color: INK },
  // Header sublines, the alert sentence, button labels (600).
  body: { ...ui, fontSize: 14, fontWeight: 400, lineHeight: 1.5, color: INK },
  bodyStrong: { ...ui, fontSize: 14, fontWeight: 600, lineHeight: 1.5, color: INK },
  // Row titles: conditions, gating items, scheduling entries, document slots.
  row: { ...ui, fontSize: 12.5, fontWeight: 500, lineHeight: 1.45, color: SLATE },
  rowBlocking: { ...ui, fontSize: 12.5, fontWeight: 550, lineHeight: 1.45, color: status.error.solid },
  rowDone: { ...ui, fontSize: 12.5, fontWeight: 450, lineHeight: 1.45, color: MUTED },
  // 13px / 400 / slate — the header address line, "Closing · not yet ready".
  sub: { ...ui, fontSize: 13, fontWeight: 400, lineHeight: 1.45, color: SLATE },
  // Helper lines, row second lines, owner attributions.
  meta: { ...ui, fontSize: 11.5, fontWeight: 400, lineHeight: 1.45, color: MUTED },
  metaStrong: { ...ui, fontSize: 11.5, fontWeight: 600, lineHeight: 1.45, color: SLATE },
  // Group labels ("External", "Internal").
  group: { ...ui, fontSize: 12, fontWeight: 700, lineHeight: 1.45, color: SLATE },
  // Chips, badges, mono field tags — the floor; nothing renders below 10px.
  micro: { ...ui, fontSize: 10, fontWeight: 650, lineHeight: 1.4 },
  // Section headers: small, caps, tracked — landmarks by treatment, not size.
  sectionH: {
    ...ui,
    fontSize: 12.5,
    fontWeight: 700,
    lineHeight: 1.4,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: SLATE,
  },
  // Accordion titles inside Readiness ("Scheduling entries") — sentence case,
  // a shade of tracking, in ink: a card label, not a landmark.
  accordionH: { ...ui, fontSize: 11.5, fontWeight: 600, lineHeight: 1.4, letterSpacing: "0.02em", color: INK },
  // "Expand all", "Show N cleared" toggles.
  toggle: { ...ui, fontSize: 12.5, fontWeight: 600, lineHeight: 1.4, color: SLATE },
  toggleAccent: { ...ui, fontSize: 12.5, fontWeight: 600, lineHeight: 1.4, color: teal[700] },
  // Expanded detail: never smaller than the row it expands from.
  detail: { ...ui, fontSize: 12.5, fontWeight: 400, lineHeight: 1.55, color: SLATE },
  // Machine values: ids, fractions, field names, dates in dense rows.
  mono: { ...mono, fontSize: 11.5, fontWeight: 500, lineHeight: 1.45, color: SLATE },
  monoId: { ...mono, fontSize: 11.5, fontWeight: 500, lineHeight: 1.4, color: MUTED },
  monoTag: { ...mono, fontSize: 12.5, fontWeight: 500, lineHeight: 1.45 },
  // Chip text in mono (counts / fractions in section headers).
  microMono: { ...mono, fontSize: 10, fontWeight: 650, lineHeight: 1.4 },
};

export default TYPE;
